import React from 'react';

interface JobFormData {
  title: string;
  department: string;
  location: string;
  type: 'Full-time' | 'Part-time' | 'Contract' | 'Internship';
  salary: string;
  postedDate: string;
  deadline: string;
  status: 'Open' | 'Closed' | 'Draft';
  description: string;
  useSeafarerForm?: boolean;
}

interface JobDetailsFieldsProps {
  formData: JobFormData;
  onInputChange: (field: string, value: string | boolean) => void;
}

const DEPARTMENTS = [
  'Office',
  'Engineering',
  'Navigation',
  'Operations',
  'Administration',
  'Finance',
  'Human Resources',
  'Safety',
  'Quality',
  'Deck Department',
  'Engine Department',
  'General Purpose',
];

const JobDetailsFields: React.FC<JobDetailsFieldsProps> = ({ formData, onInputChange }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Job Title *
        </label>
        <input
          type="text"
          required
          value={formData.title}
          onChange={(e) => onInputChange('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g. Marine Engineer"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Department *
        </label>
        <select
          required
          value={formData.department}
          onChange={(e) => onInputChange('department', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select Department</option>
          {DEPARTMENTS.map((dept) => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
          {formData.department && !DEPARTMENTS.includes(formData.department) && (
            <option value={formData.department}>{formData.department}</option>
          )}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Location *
        </label>
        <input
          type="text"
          required
          value={formData.location}
          onChange={(e) => onInputChange('location', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g. Alexandria, Egypt or Offshore Vessels"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Employment Type *
        </label>
        <select
          required
          value={formData.type}
          onChange={(e) => onInputChange('type', e.target.value as any)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="Full-time">Full-time</option>
          <option value="Part-time">Part-time</option>
          <option value="Contract">Contract</option>
          <option value="Internship">Internship</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Salary Range
        </label>
        <input
          type="text"
          value={formData.salary}
          onChange={(e) => onInputChange('salary', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g. $3000 - $4000"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Status *
        </label>
        <select
          required
          value={formData.status}
          onChange={(e) => onInputChange('status', e.target.value as any)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="Draft">Draft</option>
          <option value="Open">Open</option>
          <option value="Closed">Closed</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Posted Date *
        </label>
        <input
          type="date"
          required
          value={formData.postedDate}
          onChange={(e) => onInputChange('postedDate', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Application Deadline *
        </label>
        <input
          type="date"
          required
          value={formData.deadline}
          onChange={(e) => onInputChange('deadline', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="md:col-span-2 flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <input
          id="useSeafarerForm"
          type="checkbox"
          checked={!!formData.useSeafarerForm}
          onChange={(e) => onInputChange('useSeafarerForm', e.target.checked)}
          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="useSeafarerForm" className="text-sm text-gray-700">
          <span className="font-medium">Form-based application (Seafarer Form)</span>
          <span className="block text-gray-500 mt-1">
            When checked, the website Apply button opens the full seafarer application form for this job, and the submission is stored under Candidates instead of Hiring → Applications.
          </span>
        </label>
      </div>
    </div>
  );
};

export default JobDetailsFields;
